
import React, { useState, useEffect } from 'react';
import { Product, SiteSettings, HeroSlide, NewsItem } from '../types';
import { Edit, Trash2, ArrowRight, ChevronLeft, ChevronRight, Zap, Newspaper, Clock, Calendar, BookOpen, ExternalLink } from 'lucide-react';
import { fixImageUrl, getArticleUrl } from '../constants';

interface HeroProps {
  products: Product[];
  news?: NewsItem[];
  settings?: SiteSettings;
  isAdmin: boolean;
  onEdit: (product: Product) => void;
  onDelete: (id: string) => void;
  onTrack: (type: 'product' | 'news', id: string) => void;
  onClick?: (product: Product) => void;
  onReadArticle?: (article: NewsItem) => void;
}

const Hero: React.FC<HeroProps> = ({ products, news, settings, isAdmin, onEdit, onDelete, onTrack, onClick, onReadArticle }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;
    const diff = touchStartX - touchEndX;
    const minSwipe = 50;
    if (diff > minSwipe) {
      handleNext();
    } else if (diff < -minSwipe) {
      handlePrev();
    }
    setTouchStartX(null);
    setTouchEndX(null);
  };

  const useCustomSlides = settings?.enableCustomHero;
  const customSlides = settings?.customHeroSlides?.filter(s => s.active) || [];
  
  // Sort slides by order
  const sortedCustomSlides = [...customSlides].sort((a, b) => (a.order || 0) - (b.order || 0));

  // Determine what list to use
  let heroItems: (Product | HeroSlide | NewsItem)[] = [];
  let isNewsSlide = false;

  if (useCustomSlides && sortedCustomSlides.length > 0) {
    heroItems = sortedCustomSlides;
  } else if (products && products.length > 0) {
    heroItems = products;
  } else if (news && news.length > 0) {
    // If no product is selected, it automatically pulls the five most recent posts
    heroItems = news.slice(0, 5);
    isNewsSlide = true;
  }

  const getFirstImageFromContent = (item: NewsItem): string | null => {
    if (item.imageUrl) return item.imageUrl;
    
    // Try to extract first image URL from HTML content
    const htmlMatch = item.content?.match(/<img[^>]+src="([^">]+)"/i);
    if (htmlMatch && htmlMatch[1]) return htmlMatch[1];
    
    // Try to extract first image URL from Markdown format
    const mdMatch = item.content?.match(/!\[.*?\]\((.*?)\)/);
    if (mdMatch && mdMatch[1]) return mdMatch[1];
    
    // Try gallery images
    if (item.gallery_images && item.gallery_images.length > 0) {
      return item.gallery_images[0];
    }
    
    return null;
  };

  const calculateReadTime = (content?: string) => {
    if (!content) return '3 min';
    const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const minutes = Math.max(1, Math.ceil(words / 200));
    return `${minutes} min de leitura`;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return null;
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (e) {
      return null;
    }
  };

  const handleNewsClick = (item: NewsItem) => {
    onTrack('news', item.id);
    if (onReadArticle) {
      onReadArticle(item);
    } else {
      window.location.href = getArticleUrl(item.slug || item.id, item.category);
    }
  };

  if (!heroItems || heroItems.length === 0) {
    return (
      <div className="relative w-full h-[450px] md:h-[550px] bg-slate-900 border-b border-gray-100 flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 opacity-90"></div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center space-y-6 animate-pulse">
          <div className="flex gap-2">
            <div className="w-24 h-6 bg-slate-800 rounded-md"></div>
          </div>
          <div className="space-y-3">
            <div className="w-3/4 max-w-2xl h-12 bg-slate-800 rounded-lg"></div>
            <div className="w-1/2 max-w-md h-12 bg-slate-800 rounded-lg"></div>
          </div>
          <div className="w-5/6 max-w-xl h-6 bg-slate-800 rounded-md"></div>
          <div className="flex gap-4 pt-2">
            <div className="w-40 h-12 bg-slate-800 rounded-lg"></div>
            <div className="w-36 h-12 bg-slate-800 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  const handleProductClick = (product: Product) => {
    onTrack('product', product.id);
    if (onClick) {
      onClick(product);
    }
  };

  useEffect(() => {
    setCurrentIndex(0); // Reset index if source changes
  }, [useCustomSlides, heroItems.length]);

  useEffect(() => {
    if (isPaused || heroItems.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % heroItems.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [heroItems.length, isPaused]);

  const handlePrev = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? heroItems.length - 1 : prevIndex - 1));
  };

  const handleNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex((prevIndex) => (prevIndex + 1) % heroItems.length);
  };

  const renderProductContent = (product: Product, index: number) => {
    const cleanDesc = product.description ? product.description.replace(/<[^>]*>/g, '').trim() : '';

    return (
      <div className={`max-w-2xl lg:max-w-3xl transition-all duration-1000 delay-300 transform ${index === currentIndex ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
        <div className="p-6 md:p-8 rounded-3xl bg-black/40 backdrop-blur-md border border-white/15 shadow-2xl space-y-4">
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 bg-primary text-white text-[10px] font-black uppercase tracking-wider rounded-lg shadow-md flex items-center gap-1.5">
              <Zap size={12} fill="currentColor" /> {product.category === 'featured' ? 'Destaque' : 'Solução / Produto'}
            </span>
          </div>

          <button 
            onClick={() => handleProductClick(product)}
            className="text-left block group/hero-title w-full"
          >
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-white leading-snug tracking-tight drop-shadow-lg line-clamp-2 md:line-clamp-3 group-hover/hero-title:text-primary transition-colors">
              {product.title}
            </h1>
          </button>
          
          {cleanDesc && (
            <p className="text-gray-200 text-xs sm:text-sm md:text-base leading-relaxed font-normal line-clamp-2 text-slate-200/90">
              {cleanDesc}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button 
              onClick={() => handleProductClick(product)}
              className="px-5 py-3 md:px-7 md:py-3.5 bg-primary text-white hover:bg-primaryDark font-extrabold rounded-xl flex items-center gap-2 transition-all transform hover:scale-105 active:scale-95 shadow-xl text-xs uppercase tracking-wider cursor-pointer"
            >
              Ver Detalhes <ArrowRight size={15} />
            </button>
            
            {product.link && (
              <a 
                href={product.link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onTrack('product', product.id)}
                className="px-5 py-3 md:px-6 md:py-3.5 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl flex items-center gap-2 transition-all border border-white/20 text-xs uppercase tracking-wider backdrop-blur-md shadow-md cursor-pointer"
              >
                <ExternalLink size={15} /> Site Oficial
              </a>
            )}
            
            {isAdmin && (
              <div className="flex gap-2 ml-auto">
                <button 
                  onClick={(e) => { e.stopPropagation(); onEdit(product); }}
                  className="p-3 bg-white/10 hover:bg-blue-600 text-white rounded-xl backdrop-blur-md transition-all border border-white/10 cursor-pointer"
                  title="Editar"
                >
                  <Edit size={16} />
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); onDelete(product.id); }}
                  className="p-3 bg-white/10 hover:bg-red-600 text-white rounded-xl backdrop-blur-md transition-all border border-white/10 cursor-pointer"
                  title="Excluir"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderNewsContent = (item: NewsItem, index: number) => {
    const cleanExcerpt = item.excerpt || (item.content ? item.content.replace(/<[^>]*>/g, '').trim() : '');
    const readTime = item.readingTime || calculateReadTime(item.content);
    const pubDate = formatDate(item.date);

    return (
      <div className={`max-w-2xl lg:max-w-3xl transition-all duration-1000 delay-300 transform ${index === currentIndex ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
        <div className="p-6 md:p-8 rounded-3xl bg-black/40 backdrop-blur-md border border-white/15 shadow-2xl space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="px-3 py-1 bg-primary text-white text-[10px] font-black uppercase tracking-wider rounded-lg shadow-md flex items-center gap-1.5">
              <Newspaper size={12} fill="currentColor" /> {item.category || 'Matéria'}
            </span>
            <span className="px-2.5 py-1 bg-white/10 backdrop-blur-md text-white/90 text-[10px] font-bold rounded-lg border border-white/15 flex items-center gap-1">
              <Clock size={12} className="text-primary" /> {readTime}
            </span>
            {pubDate && (
              <span className="px-2.5 py-1 bg-white/10 backdrop-blur-md text-white/80 text-[10px] font-medium rounded-lg border border-white/15 hidden sm:inline-flex items-center gap-1">
                <Calendar size={12} /> {pubDate}
              </span>
            )}
          </div>

          <button 
            onClick={() => handleNewsClick(item)}
            className="text-left block group/hero-title w-full"
          >
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-white leading-snug tracking-tight drop-shadow-lg line-clamp-2 md:line-clamp-3 group-hover/hero-title:text-primary transition-colors">
              {item.title}
            </h1>
          </button>
          
          {cleanExcerpt && (
            <p className="text-gray-200 text-xs sm:text-sm md:text-base leading-relaxed font-normal line-clamp-2 text-slate-200/90">
              {cleanExcerpt}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button 
              onClick={() => handleNewsClick(item)}
              className="px-5 py-3 md:px-7 md:py-3.5 bg-primary text-white hover:bg-primaryDark font-extrabold rounded-xl flex items-center gap-2 transition-all transform hover:scale-105 active:scale-95 shadow-xl text-xs uppercase tracking-wider cursor-pointer"
            >
              <BookOpen size={15} /> Ler Matéria Completa <ArrowRight size={15} />
            </button>
            
            <a 
              href="/blog"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = '/blog';
              }}
              className="px-5 py-3 md:px-6 md:py-3.5 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl flex items-center gap-2 transition-all border border-white/20 text-xs uppercase tracking-wider backdrop-blur-md shadow-md cursor-pointer"
            >
              Ver Blog
            </a>
          </div>
        </div>
      </div>
    );
  };

  const renderCustomSlideContent = (slide: HeroSlide, index: number) => (
    <div className={`max-w-2xl lg:max-w-3xl transition-all duration-1000 delay-300 transform ${index === currentIndex ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
      <div className="p-6 md:p-8 rounded-3xl bg-black/40 backdrop-blur-md border border-white/15 shadow-2xl space-y-4">
        <h1 
          className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-white leading-snug tracking-tight drop-shadow-lg line-clamp-2 md:line-clamp-3"
          dangerouslySetInnerHTML={{ __html: slide.title }}
        />
        
        {slide.subtitle && (
          <p 
            className="text-gray-200 text-xs sm:text-sm md:text-base leading-relaxed font-normal line-clamp-2 text-slate-200/90"
            dangerouslySetInnerHTML={{ __html: slide.subtitle }}
          />
        )}

        {slide.link && slide.buttonText && (
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <a 
              href={slide.link}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-3 md:px-7 md:py-3.5 bg-primary text-white hover:bg-primaryDark font-extrabold rounded-xl flex items-center gap-2 transition-all transform hover:scale-105 active:scale-95 shadow-xl text-xs uppercase tracking-wider cursor-pointer"
            >
              {slide.buttonText} <ArrowRight size={15} />
            </a>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div 
      className="relative w-full h-[480px] md:h-[560px] overflow-hidden mb-2 bg-slate-950 group border-b border-gray-100"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {heroItems.map((item, index) => {
        const isCustom = useCustomSlides && sortedCustomSlides.length > 0;
        const isNews = !isCustom && products.length === 0 && news && news.length > 0;

        let imageUrl = '';
        if (isCustom) {
          imageUrl = fixImageUrl((item as HeroSlide).imageUrl, settings);
        } else if (isNews) {
          imageUrl = fixImageUrl(getFirstImageFromContent(item as NewsItem) || '', settings) || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&q=80&w=1600';
        } else {
          const prod = item as Product;
          imageUrl = prod.bannerImageUrl ? prod.bannerImageUrl : prod.imageUrl;
        }

        return (
          <div
            key={item.id}
            className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
              index === currentIndex ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-105 z-0'
            }`}
          >
            {/* Background Image with Ken Burns Effect */}
            <div 
              className="absolute inset-0 bg-cover bg-center transition-transform duration-[10000ms] ease-out"
              style={{ 
                backgroundImage: `url(${imageUrl})`,
                transform: index === currentIndex && !isPaused ? 'scale(1.1)' : 'scale(1.0)'
              }}
            ></div>
            
            {/* Overlays for dark readability */}
            <div className="absolute inset-0 bg-black/40"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>

            {/* Content */}
            <div className="relative h-full flex flex-col justify-center max-w-7xl mx-auto px-6 md:px-12">
              {isCustom 
                ? renderCustomSlideContent(item as HeroSlide, index)
                : isNews
                  ? renderNewsContent(item as NewsItem, index)
                  : renderProductContent(item as Product, index)
              }
            </div>
          </div>
        );
      })}

      {/* Side Navigation Arrows */}
      {heroItems.length > 1 && (
        <div className="hidden md:flex absolute inset-y-0 left-0 right-0 z-30 items-center justify-between px-4 pointer-events-none">
          <button 
            onClick={(e) => handlePrev(e)}
            className="pointer-events-auto p-3 rounded-xl bg-black/30 hover:bg-primary text-white border border-white/15 transition-all opacity-0 group-hover:opacity-100 active:scale-90 backdrop-blur-md cursor-pointer"
          >
            <ChevronLeft size={24} />
          </button>
          <button 
            onClick={(e) => handleNext(e)}
            className="pointer-events-auto p-3 rounded-xl bg-black/30 hover:bg-primary text-white border border-white/15 transition-all opacity-0 group-hover:opacity-100 active:scale-90 backdrop-blur-md cursor-pointer"
          >
            <ChevronRight size={24} />
          </button>
        </div>
      )}

      {/* Slide Indicators */}
      {heroItems.length > 1 && (
        <div className="absolute bottom-6 left-6 md:left-12 z-30 flex items-center gap-4">
          <div className="flex gap-1.5">
             {heroItems.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`h-1.5 rounded-full transition-all duration-500 cursor-pointer ${
                    idx === currentIndex ? 'w-8 bg-primary' : 'w-2 bg-white/30 hover:bg-white/60'
                  }`}
                />
              ))}
          </div>
          <div className="bg-black/50 backdrop-blur-md rounded-lg px-3 py-1 border border-white/15 text-[10px] font-black text-white/70">
            <span className="text-white">{(currentIndex + 1)}</span> / {heroItems.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default Hero;

